import { useMemo } from 'react';
import { useGetRecentCallsQuery, useGetVoicemailsByExtensionQuery } from '../services/VoipService';
import useLastRecentsViewedAt from './useLastRecentsViewedAt';
import useIsVoipUser from './useIsVoipUser';

/**
 * Centralised badge counts for the RhinoVOIP notification feature.
 *
 * Returns:
 *   newMissedCount  — inbound missed/no_answer calls whose start time is after
 *                     the last time the member opened the Recents tab.
 *   unreadVmCount   — voicemails with message_status NULL/'unread'.
 *   totalNewCount   — sum of the two, used by the nav badge.
 *
 * Queries are skipped (no network traffic) for members who don't have a VoIP
 * extension or whose org has VoIP disabled — same gating used elsewhere.
 */
export default function useVoipBadgeCounts() {
  const { isVoipUser: enabled } = useIsVoipUser();

  const { lastViewedAtMs } = useLastRecentsViewedAt();

  // Poll every 10s so the nav badge picks up new missed calls / voicemails
  // at the same cadence the Recents and Voicemail tabs refresh.
  const POLL_MS = 10_000;
  const { data: recentCallsData } = useGetRecentCallsQuery(true, {
    skip: !enabled,
    pollingInterval: POLL_MS,
    refetchOnMountOrArgChange: true,
  });
  const { data: voicemailsData } = useGetVoicemailsByExtensionQuery(true, {
    skip: !enabled,
    pollingInterval: POLL_MS,
    refetchOnMountOrArgChange: true,
  });

  const newMissedCount = useMemo(() => {
    if (!enabled) return 0;
    const records = Array.isArray(recentCallsData?.objects) ? recentCallsData.objects : [];
    return records.reduce((count, r) => {
      if ((r.direction || '').toLowerCase() !== 'inbound') return count;
      // Any inbound call the member didn't answer counts as missed for the
      // Recents badge — including ones that rolled to voicemail.
      const statusLower = (r.status || '').toLowerCase();
      if (statusLower === 'answered') return count;
      // Prefer start_epoch (unix seconds, timezone-unambiguous) and fall back
      // to start_stamp if it's missing.
      let ts = 0;
      if (r.start_epoch != null) {
        const n = Number(r.start_epoch);
        if (Number.isFinite(n)) ts = n * 1000;
      }
      if (!ts && r.start_stamp) {
        const parsed = new Date(r.start_stamp).getTime();
        if (Number.isFinite(parsed)) ts = parsed;
      }
      if (!ts || ts <= lastViewedAtMs) return count;
      return count + 1;
    }, 0);
  }, [enabled, recentCallsData, lastViewedAtMs]);

  const unreadVmCount = useMemo(() => {
    if (!enabled) return 0;
    const records = Array.isArray(voicemailsData?.objects) ? voicemailsData.objects : [];
    return records.reduce((count, vm) => {
      const status = vm.message_status;
      const isUnread = status == null || status === '' || status === 'unread';
      return isUnread ? count + 1 : count;
    }, 0);
  }, [enabled, voicemailsData]);

  return {
    newMissedCount,
    unreadVmCount,
    totalNewCount: newMissedCount + unreadVmCount,
  };
}
